import { Component, Input, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
/**
 * Componente que representa un campo de entrada personalizable para formularios.
 */
@Component({
    selector: 'am-input-form',
    templateUrl: './am-input-form.component.html',
    styleUrls: ['./am-input-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AmInputFormComponent),
            multi: true,
        },
    ],
    standalone: false
})

export class AmInputFormComponent implements ControlValueAccessor {

  /**
   * Clase CSS personalizada para el contenedor del campo de entrada.
   *
   * @type {string}
   * @default ''
   */
  @Input() className = '';

  /**
   * Clase CSS personalizada para el ícono del campo de entrada.
   *
   * @type {string}
   * @default 'AmInputForm-formCorrect'
   */
  @Input() classNameIcon = 'AmInputForm-formCorrect';

  /**
   * Clase CSS personalizada para el mensaje de error.
   *
   * @type {string}
   * @default 'AmInputForm-formCorrect'
   */
  @Input() classNameMessage = 'AmInputForm-formCorrect';

  /**
   * Mensaje de error cuando el valor no coincide con el patrón esperado.
   *
   * @type {string}
   * @default ''
   */
  @Input() errorPattern = '';

  /**
   * Mensaje de error cuando el campo es requerido pero no se ha ingresado ningún valor.
   *
   * @type {string}
   * @default ''
   */
  @Input() errorRequired = '';

  /**
   * Grupo de formulario al que pertenece el campo de entrada.
   *
   * @type {FormGroup}
   */
  @Input() formGroup: FormGroup;

  /**
   * Indica si se muestra un ícono para eliminar el valor del campo.
   *
   * @type {boolean}
   * @default true
   */
  @Input() iconDelete = true;

  /**
   * Indica si el campo de entrada debe ocupar todo el ancho disponible.
   *
   * @type {boolean}
   * @default false
   */
  @Input() inputFullSize = false;

  /**
   * Indica si se muestra una flecha roja en el campo de entrada.
   *
   * @type {boolean}
   * @default false
   */
  @Input() showArrowRed = false;

  /**
   * Etiqueta principal del campo de entrada.
   *
   * @type {string}
   * @default ''
   */
  @Input() label = '';

  /**
   * Segunda etiqueta opcional del campo de entrada.
   *
   * @type {string}
   * @default ''
   */
  @Input() label2 = '';

  /**
   * Longitud máxima permitida para el valor del campo de entrada.
   *
   * @type {number}
   * @default 99999
   */
  @Input() maxlength = 99999;

  /**
   * Nombre del campo de entrada.
   *
   * @type {string}
   * @default ''
   */
  @Input() nameInput = '';

  /**
   * Nombre de la lista asociada al campo de entrada (opcional).
   *
   * @type {any}
   */
  @Input() nameList: any;

  /**
   * Texto de marcador de posición (placeholder) del campo de entrada.
   *
   * @type {string}
   * @default ''
   */
  @Input() placeholder = '';

  /**
   * Control del formulario asociado al campo de entrada.
   *
   * @type {any}
   */
  @Input() textControl: any;

  /**
   * Tipo de campo de entrada (text, number, email, etc.).
   *
   * @type {string}
   * @default 'text'
   */
  @Input() typeInput = 'text';

  /**
   * Indica si el campo de entrada es de solo lectura.
   *
   * @type {boolean}
   * @default false
   */
  @Input() readOnly: boolean = false;

  /**
   * Valor actual del campo de entrada.
   *
   * @type {string}
   */
  val: string;

  /**
   * Indica si la etiqueta está activa (para efectos visuales).
   *
   * @type {boolean}
   */
  islabel: boolean = false;

  constructor() {}

/**
   * Método de la interfaz `ControlValueAccessor` que escribe un valor en el campo de entrada.
   *
   * @param {any} value - Valor a escribir en el campo.
   * @return {void} No retorna ningún valor.
   */
  writeValue(value: any): void {
    this.value = value;
  }
    /**
   * Método de la interfaz `ControlValueAccessor` que registra una función para notificar cambios en el valor del campo.
   *
   * @param {any} fn - Función de callback para notificar cambios.
   * @return {void} No retorna ningún valor.
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
    /**
   * Método de la interfaz `ControlValueAccessor` que registra una función para notificar que el campo fue tocado.
   *
   * @param {any} fn - Función de callback para notificar que el campo fue tocado.
   * @return {void} No retorna ningún valor.
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
   /**
   * Método de la interfaz `ControlValueAccessor` que habilita o deshabilita el campo de entrada.
   *
   * @param {boolean} isDisabled - Indica si el campo debe estar deshabilitado.
   * @return {void} No retorna ningún valor.
   */
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.textControl.disable();
    } else {
      this.textControl.enable();
    }
  }
  

  onChange: any = () => {};
  onTouched: any = () => {};

  onTouchedInp() {
    this.islabel = !this.islabel;
  }

  /**
   * Setter para el valor del campo de entrada.
   * Actualiza el valor y notifica los cambios a través de `onChange` y `onTouched`.
   *
   * @param {string} val - Nuevo valor del campo.
   * @return {void} No retorna ningún valor.
   */

  set value(val){
    this.val = val
    this.onChange(val)
    if(val){
      this.onTouched(val)
    }
  }
  /**
   * Método que se ejecuta cuando se hace clic en el ícono de eliminar.
   * Borra el valor del campo de entrada.
   *
   * @return {void} No retorna ningún valor.
   */
  onDelete(){
    this.value = ''
  }
}
